import { useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";

// Factures du client dont le montant ne correspond à aucun contrat : l'utilisateur décide.
export default function ConfirmLinksDialog({ data, contractId, onClose, onDone }) {
  const [picked, setPicked] = useState({});
  const [targets, setTargets] = useState([contractId]);
  const [busy, setBusy] = useState(false);
  const multi = data.group_contracts.length > 1;
  const ids = Object.keys(picked).filter((k) => picked[k]);

  const toggleTarget = (id) => setTargets((t) =>
    t.includes(id) ? t.filter((x) => x !== id) : [...t, id]);

  const submit = async () => {
    setBusy(true);
    let ok = 0;
    for (const id of ids) {
      try {
        await api.post(`/contracts/${contractId}/link-invoice`,
          { invoice_id: id, contract_ids: targets });
        ok += 1;
      } catch (e) { toast.error(apiError(e)); }
    }
    setBusy(false);
    toast.success(`${ok} facture(s) liée(s)`);
    onDone();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bridge-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Montants non concordants ({data.to_confirm_count})</DialogTitle>
          <DialogDescription>
            Ces factures du client ne correspondent à aucun montant de contrat
            ({fmtEUR(data.contract_amount_ht)} HT pour celui-ci). Coche celles à rapprocher quand même.
          </DialogDescription>
        </DialogHeader>
        {multi && (
          <div className="rounded border p-2 space-y-1" data-testid="bridge-confirm-targets">
            <div className="text-xs font-medium text-slate-600">Lier aux contrats :</div>
            {data.group_contracts.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-xs">
                <Checkbox checked={targets.includes(c.id)} disabled={c.id === contractId}
                          onCheckedChange={() => toggleTarget(c.id)}
                          data-testid={`bridge-confirm-target-${c.id}`} />
                {c.title || "Contrat"} · {fmtEUR(c.amount_ht)} HT
                {c.id === contractId && <span className="text-slate-400">(ce contrat)</span>}
              </label>
            ))}
          </div>
        )}
        <div className="max-h-80 overflow-y-auto divide-y border rounded">
          {data.to_confirm.map((inv) => (
            <label key={inv.id} className="flex items-center gap-3 p-2 text-xs hover:bg-slate-50">
              <Checkbox checked={!!picked[inv.id]}
                        onCheckedChange={(v) => setPicked((p) => ({ ...p, [inv.id]: !!v }))}
                        data-testid={`bridge-confirm-inv-${inv.id}`} />
              <span className="font-medium w-28 truncate">{inv.number || "sans n°"}</span>
              <span className="text-slate-500 w-20">{fmtDate(inv.invoice_date)}</span>
              <span className="flex-1 truncate text-slate-500">{inv.party_name}</span>
              <span className="font-mono">{fmtEUR(inv.amount_ht)} HT</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="bridge-confirm-cancel">Ignorer</Button>
          <Button onClick={submit} disabled={!ids.length || busy} data-testid="bridge-confirm-submit">
            {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Lier {ids.length || ""} facture(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
